import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["fields"],
  _excluded2 = ["renderer", "className", "schema"],
  _excluded3 = ["handleChangeOperator", "handleOnBlur", "handleOnChange", "handleOnChangeGroup", "handleOnDelete", "innerValues"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, Fragment } from 'react';
import classNames from 'classnames';
import { useTheme, FieldGroup, Icon, Input, Section, Select, Tooltip } from 'hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { getCrossID } from "../../../utils/filters";
import { getRange } from "../../../utils/time";
import FiltersSection from "../FiltersSection";
import { defaultPlaceholders, defaultValues, fieldsComponent, inputFields, rangeFields, customFieldsComponent } from "../constants";
import { operators } from "../operators";
import useAdvancedFilters from "./useAdvancedFilters";
const {
  EMPTY,
  NOT_EMPTY,
  GREATER,
  GREATER_OR_EQUAL,
  LESS,
  LESS_OR_EQUAL,
  TODAY,
  YESTERDAY,
  TOMORROW,
  THIS_WEEK,
  LAST_WEEK,
  NEXT_WEEK,
  THIS_MONTH,
  LAST_MONTH,
  NEXT_MONTH,
  THIS_QUARTER,
  LAST_QUARTER,
  NEXT_QUARTER,
  THIS_YEAR,
  LAST_YEAR,
  NEXT_YEAR
} = operators;
var _ref5 = /*#__PURE__*/React.createElement(Icon, {
  name: "refresh",
  size: "medium"
});
const useFilters = _ref => {
  let {
    classes = {},
    crossOperators,
    entity,
    getAllowedOperators = () => {},
    getFieldsWithoutAdvancedFilters = () => {},
    getHasAdvancedFilters = () => {},
    isPinnable,
    onToggle,
    toggleQuickFilter,
    operators,
    schema,
    toggled,
    useSections,
    values,
    fromQuickFilters = false
  } = _ref;
  const {
    language,
    t
  } = useWeb5Context();
  const theme = useTheme();
  const {
    getFieldOperator,
    getOperatorValues,
    hasAdvancedFilters
  } = useAdvancedFilters({
    operators,
    crossOperators,
    getAllowedOperators,
    getFieldsWithoutAdvancedFilters,
    getHasAdvancedFilters
  });
  const getActiveFields = useCallback(filter => {
    let activeFields = 0;
    const {
      id,
      fields,
      entity: crossEntity,
      isFakeCrossFilter
    } = filter;
    const finalEntity = !!crossEntity && !isFakeCrossFilter ? crossEntity : null;
    const ID = getCrossID(id, finalEntity);
    fields.forEach(current => {
      var _current$inputAttrs, _current$inputAttrs$a;
      const value = (values === null || values === void 0 ? void 0 : values[ID]) || null;
      if (value) {
        if (Array.isArray(value) && !rangeFields[current.dataType]) activeFields = value.length;else activeFields = 1;
      }

      // There are fields that contain actions that activate other filters (p.e. activity's isSuspiciousCheckIn)
      // and we need to add thosee filters to the active fields count
      if ((_current$inputAttrs = current.inputAttrs) !== null && _current$inputAttrs !== void 0 && (_current$inputAttrs$a = _current$inputAttrs.actions) !== null && _current$inputAttrs$a !== void 0 && _current$inputAttrs$a.length) {
        var _current$inputAttrs2;
        (_current$inputAttrs2 = current.inputAttrs) === null || _current$inputAttrs2 === void 0 ? void 0 : _current$inputAttrs2.actions.forEach(action => {
          const actionID = getCrossID(action.id, finalEntity);
          if (values[actionID]) {
            activeFields = activeFields + 1;
          }
        });
      }
    });

    // Show 1 active field if only advanced filters 'Empty' or 'Not Empty' are active
    const currentOperators = !!finalEntity ? (crossOperators === null || crossOperators === void 0 ? void 0 : crossOperators[finalEntity]) || {} : operators;
    const operator = currentOperators !== null && currentOperators !== void 0 && currentOperators[id] ? currentOperators[id].value : undefined;
    if (activeFields === 0 && operator && [EMPTY, NOT_EMPTY].includes(operator)) activeFields = 1;
    return activeFields;
  }, [crossOperators, operators, values]);
  const getCommonProps = useCallback((field, crossEntity) => {
    const key = getCrossID(field.id, crossEntity);
    return _objectSpread({
      key: `${key}_fieldGroup`,
      label: field.label,
      labelMode: 'vertical'
    }, field.inputProps);
  }, []);
  const getFieldProps = useCallback(_ref2 => {
    let {
      field,
      operator,
      crossEntity,
      onChange,
      onBlur,
      innerValues
    } = _ref2;
    if (!field.inputProps) field.inputProps = {};
    const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    const crossId = getCrossID(field.id, finalEntity);
    let commonProps = getCommonProps(field, finalEntity);
    let defaultValue = defaultValues[field.dataType];
    if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];
    let finalValue = (innerValues === null || innerValues === void 0 ? void 0 : innerValues[crossId]) || defaultValue;
    let propsByField = _objectSpread(_objectSpread({}, commonProps), {}, {
      value: finalValue,
      onChange: onChange && onChange({
        field,
        crossEntity: finalEntity
      }),
      isFullWidth: true
    });
    const advancedFilters = getHasAdvancedFilters(finalEntity);
    const isDisabled = advancedFilters && [EMPTY, NOT_EMPTY, TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR].includes(operator);
    switch (field.dataType) {
      case 'bool':
        propsByField = _objectSpread(_objectSpread({}, propsByField), {}, {
          placeholder: t('label_selectone'),
          labelMode: 'horizontal',
          isReadOnly: isDisabled
        });
        break;
      case 'probability':
        const isLessOperator = [LESS, LESS_OR_EQUAL].includes(operator);
        const isGreaterOperator = [GREATER, GREATER_OR_EQUAL].includes(operator);
        const isRange = !isLessOperator && !isGreaterOperator;
        propsByField = _objectSpread(_objectSpread({}, propsByField), {}, {
          min: 0,
          max: 100,
          step: 10,
          isRange: advancedFilters ? isRange : true,
          reverse: advancedFilters && isGreaterOperator,
          isReadOnly: isDisabled,
          classes: {
            root: classes.slider
          }
        });
        if (isLessOperator) {
          propsByField.value = finalValue ? finalValue[0] : 0;
        }
        if (isGreaterOperator) {
          propsByField.value = finalValue ? finalValue[1] : 100;
        }
        if (isRange) propsByField.value = finalValue;
        break;
      case 'singleValueList':
      case 'multipleValueList':
      case 'relatedValueList':
        propsByField = _objectSpread(_objectSpread({}, propsByField), {}, {
          placeholder: t('label_selectone'),
          isReadOnly: isDisabled,
          highlightMatch: propsByField.isFuzzy
        });
        break;
      case 'text':
        propsByField.type = field.dataType;
        propsByField.onBlur = onBlur && onBlur(field, finalEntity);
        propsByField.onEnter = onBlur && onBlur(field, finalEntity);
        propsByField.isReadOnly = isDisabled;
        if (!propsByField.placeholder) {
          propsByField.placeholder = defaultPlaceholders[field.dataType] || t('placeholder_text_field') || '';
        }
        break;
      case 'singleDate':
        propsByField = _objectSpread(_objectSpread({}, propsByField), {}, {
          type: field.dataType,
          isReadOnly: isDisabled
        });
        break;
      default:
        propsByField.type = field.dataType === 'quantity' ? 'integer' : field.dataType;
        propsByField.onBlur = onBlur && onBlur(field, finalEntity);
        propsByField.isReadOnly = isDisabled;
        if (!propsByField.placeholder) {
          propsByField.placeholder = defaultPlaceholders[propsByField.type] || '';
        }
        break;
    }
    if (isDisabled) {
      propsByField.placeholder = ['decimal', 'integer'].includes(field.dataType) ? defaultPlaceholders[field.dataType] : '';
    }
    return propsByField;
  }, [getCommonProps, getHasAdvancedFilters, classes.slider, t]);
  const handleOnToggleFilter = useCallback(_ref3 => {
    let {
      filter,
      hasSubfilters,
      parentFilter
    } = _ref3;
    return () => {
      onToggle && onToggle({
        filter,
        hasSubfilters,
        parentFilter,
        fromQuickFilters
      });
    };
  }, [fromQuickFilters, onToggle]);
  const onToggleQuickFilter = useCallback(id => {
    return e => {
      e.preventDefault();
      e.stopPropagation();
      toggleQuickFilter(id);
    };
  }, [toggleQuickFilter]);
  const renderField = useCallback(_ref4 => {
    let {
      field,
      crossEntity,
      innerValues,
      handleChangeOperator,
      handleOnBlur,
      handleOnChange,
      handleOnChangeGroup,
      renderingSubfilters
    } = _ref4;
    if (!field.inputProps) field.inputProps = {};
    let FilterField = Input;
    let defaultValue = defaultValues[field.dataType];
    if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];
    const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
    const crossId = getCrossID(field.id, finalEntity);
    let finalValue = (innerValues === null || innerValues === void 0 ? void 0 : innerValues[crossId]) || defaultValue;
    const advancedFilters = getHasAdvancedFilters(finalEntity);
    let operator = null;
    let OperatorField = null;
    if (hasAdvancedFilters(_objectSpread(_objectSpread({}, field), {}, {
      crossEntity: finalEntity
    }))) {
      operator = getFieldOperator(field, finalEntity);
      const {
        options,
        value,
        defaultValue: operatorDefaultValue
      } = getOperatorValues(field.dataType, operator, finalEntity);
      const isDefaultOperator = value.value === operatorDefaultValue.value;
      const overrides = isDefaultOperator ? {
        control: {
          style: {
            borderColor: `${theme.colors.grey[100]} !important`,
            backgroundColor: theme.colors.primary.white
          }
        }
      } : {
        control: {
          style: {
            borderColor: 'transparent !important',
            backgroundColor: theme.colors.semantic.infoCustom50,
            '&:hover': {
              backgroundColor: theme.colors.semantic.infoCustom100
            },
            cursor: 'pointer'
          }
        },
        controlFocused: {
          style: {
            borderColor: 'transparent !important',
            backgroundColor: theme.colors.semantic.infoCustom100
          }
        },
        indicatorsContainer: {
          style: {
            '& svg path': {
              fill: `${theme.colors.semantic.info500} !important`
            },
            '& .hoi-poi-select__indicator-separator': {
              backgroundColor: theme.colors.semantic.info500
            }
          }
        },
        valueContainer: {
          style: {
            '& .hoi-poi-select__single-value': {
              color: theme.colors.semantic.info500
            }
          }
        }
      };
      const operatorFieldProps = {
        key: `${crossId}__operator`,
        isRequired: isDefaultOperator,
        isSearchable: false,
        isFullWidth: true,
        className: 'fm-adv-filter-operator',
        clearIcon: !isDefaultOperator ? _ref5 : null,
        onChange: handleChangeOperator && handleChangeOperator({
          field,
          defaultValue: operatorDefaultValue,
          crossEntity: finalEntity
        }),
        options,
        value,
        overrides
      };
      OperatorField = /*#__PURE__*/React.createElement(Select, operatorFieldProps);
    }
    if (field.overrideDefaultComponent) {
      const FieldComponent = customFieldsComponent[field.overrideDefaultComponent];
      if (!FieldComponent) return null;
      const fieldComponentProps = {
        crossEntity: finalEntity,
        field,
        values: innerValues,
        key: crossId,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        operator
      };
      return /*#__PURE__*/React.createElement("div", {
        key: crossId,
        className: classes.field
      }, OperatorField, /*#__PURE__*/React.createElement(FieldComponent, fieldComponentProps));
    } else if (rangeFields[field.dataType]) {
      var _field$locators, _field$locators2;
      const Field = rangeFields[field.dataType];
      if (field.dataType === 'date') {
        finalValue = [finalValue[0] || null, finalValue[1] || null];
      }
      let commonProps = getCommonProps(field, finalEntity);
      const commonRange = {
        calendarButtonLabel: t('label_today'),
        onBlur: field.dataType !== 'date' ? handleOnBlur(field, finalEntity) : null,
        placeholder: commonProps.placeholder || defaultPlaceholders[field.dataType] || t('action_select'),
        lang: language
      };
      if (inputFields.includes(field.dataType)) {
        commonRange.type = field.dataType;
      }
      const inputOneIsReadOnly = advancedFilters && [EMPTY, NOT_EMPTY, TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR, GREATER, GREATER_OR_EQUAL].includes(operator);
      const inputTwoIsReadOnly = advancedFilters && [EMPTY, NOT_EMPTY, TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR, LESS, LESS_OR_EQUAL].includes(operator);
      const inputPropsOne = _objectSpread(_objectSpread(_objectSpread({}, commonProps), commonRange), {}, {
        className: ((_field$locators = field.locators) === null || _field$locators === void 0 ? void 0 : _field$locators.first) || null,
        key: `${crossId}From`,
        isReadOnly: inputOneIsReadOnly,
        overrides: {
          flatpickrOptions: {
            position: 'auto center'
          }
        }
      });
      const inputPropsTwo = _objectSpread(_objectSpread(_objectSpread({}, commonProps), commonRange), {}, {
        className: ((_field$locators2 = field.locators) === null || _field$locators2 === void 0 ? void 0 : _field$locators2.last) || null,
        key: `${crossId}To`,
        isReadOnly: inputTwoIsReadOnly,
        overrides: {
          flatpickrOptions: {
            position: 'auto center'
          }
        }
      });
      const placeholderWhenDisabled = ['decimal', 'integer'].includes(field.dataType) ? defaultPlaceholders[field.dataType] : '';
      if (inputOneIsReadOnly) inputPropsOne.placeholder = placeholderWhenDisabled;
      if (inputTwoIsReadOnly) inputPropsTwo.placeholder = placeholderWhenDisabled;
      if (field.dataType === 'date') {
        var _inputPropsOne$maxDat, _inputPropsTwo$minDat;
        const minDate = finalValue[0] || null;
        const maxDate = finalValue[1] || null;
        inputPropsOne.maxDate = maxDate ? new Date(maxDate) : maxDate;
        inputPropsTwo.minDate = minDate ? new Date(minDate) : minDate;
        (_inputPropsOne$maxDat = inputPropsOne.maxDate) === null || _inputPropsOne$maxDat === void 0 ? void 0 : _inputPropsOne$maxDat.setHours(0, 0, 0, 0);
        (_inputPropsTwo$minDat = inputPropsTwo.minDate) === null || _inputPropsTwo$minDat === void 0 ? void 0 : _inputPropsTwo$minDat.setHours(0, 0, 0, 0);
        if (advancedFilters && [TODAY, YESTERDAY, TOMORROW, THIS_WEEK, LAST_WEEK, NEXT_WEEK, THIS_MONTH, LAST_MONTH, NEXT_MONTH, THIS_QUARTER, LAST_QUARTER, NEXT_QUARTER, THIS_YEAR, LAST_YEAR, NEXT_YEAR].includes(operator)) {
          var _currentValue$, _currentValue$$toStri, _finalValue, _finalValue$, _finalValue$$toString, _currentValue$2, _currentValue$2$toStr, _finalValue2, _finalValue2$, _finalValue2$$toStrin;
          finalValue = getRange(operator);
          const currentValue = innerValues[crossId] || values[crossId];

          // Trigger onChange to set the predefined filter values
          // for the chosen operator if needed
          if (!currentValue || currentValue && (currentValue === null || currentValue === void 0 ? void 0 : (_currentValue$ = currentValue[0]) === null || _currentValue$ === void 0 ? void 0 : (_currentValue$$toStri = _currentValue$.toString) === null || _currentValue$$toStri === void 0 ? void 0 : _currentValue$$toStri.call(_currentValue$)) !== ((_finalValue = finalValue) === null || _finalValue === void 0 ? void 0 : (_finalValue$ = _finalValue[0]) === null || _finalValue$ === void 0 ? void 0 : (_finalValue$$toString = _finalValue$.toString) === null || _finalValue$$toString === void 0 ? void 0 : _finalValue$$toString.call(_finalValue$)) || currentValue && (currentValue === null || currentValue === void 0 ? void 0 : (_currentValue$2 = currentValue[1]) === null || _currentValue$2 === void 0 ? void 0 : (_currentValue$2$toStr = _currentValue$2.toString) === null || _currentValue$2$toStr === void 0 ? void 0 : _currentValue$2$toStr.call(_currentValue$2)) !== ((_finalValue2 = finalValue) === null || _finalValue2 === void 0 ? void 0 : (_finalValue2$ = _finalValue2[1]) === null || _finalValue2$ === void 0 ? void 0 : (_finalValue2$$toStrin = _finalValue2$.toString) === null || _finalValue2$$toStrin === void 0 ? void 0 : _finalValue2$$toStrin.call(_finalValue2$))) {
            handleOnChangeGroup(field, finalEntity)(finalValue);
          }
        }
      }
      return /*#__PURE__*/React.createElement("div", {
        key: crossId,
        className: classes.field
      }, OperatorField, /*#__PURE__*/React.createElement(FieldGroup, {
        key: crossId,
        inputs: [Field, Field],
        isFullWidth: true,
        dividerText: "-",
        onChange: handleOnChangeGroup(field, finalEntity),
        value: finalValue,
        inputProps: [inputPropsOne, inputPropsTwo]
      }));
    } else {
      var _field$inputAttrs, _field$inputAttrs$act;
      const fieldProps = getFieldProps({
        field,
        operator,
        crossEntity: finalEntity,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        innerValues
      });
      const FieldComponent = fieldsComponent[field.dataType] || null;
      let actionFields = null;
      if (field !== null && field !== void 0 && (_field$inputAttrs = field.inputAttrs) !== null && _field$inputAttrs !== void 0 && (_field$inputAttrs$act = _field$inputAttrs.actions) !== null && _field$inputAttrs$act !== void 0 && _field$inputAttrs$act.length) {
        actionFields = /*#__PURE__*/React.createElement("div", {
          className: classes.subFiltersActions,
          key: `${crossId}__actions`
        }, field.inputAttrs.actions.map(action => {
          const {
            id,
            component: Component,
            onClick,
            hint
          } = action;
          const componentProps = {
            key: id,
            onClick,
            entity,
            hint
          };
          return /*#__PURE__*/React.createElement(Component, componentProps);
        }));
      }
      if (renderingSubfilters) {
        // Set prop overrides for the Select component when rendering subfilters
        if (['bool', 'singleValueList', 'multipleValueList', 'relatedValueList'].includes(field.dataType)) {
          fieldProps.useMenuPortal = false;
          fieldProps.menuPosition = 'absolute';
          fieldProps.overrides = {
            styles: {
              menu: base => _objectSpread(_objectSpread({}, base), {}, {
                position: 'relative',
                boxShadow: 'none !important',
                margin: 0,
                paddingBottom: 0
              }),
              menuList: base => _objectSpread(_objectSpread({}, base), {}, {
                paddingBottom: 0
              }, theme.utils.scrollbar)
            },
            option: {
              style: {
                borderRadius: 4
              }
            }
          };
        }
      }
      switch (field.dataType) {
        case 'bool':
        case 'probability':
        case 'singleValueList':
        case 'multipleValueList':
        case 'relatedValueList':
        case 'text':
        case 'singleDate':
          FilterField = /*#__PURE__*/React.createElement(FieldComponent, fieldProps);
          break;
        default:
          FilterField = /*#__PURE__*/React.createElement(Input, fieldProps);
          break;
      }
      return /*#__PURE__*/React.createElement("div", {
        key: crossId,
        className: classes.field
      }, OperatorField, FilterField, actionFields);
    }
  }, [classes.field, classes.subFiltersActions, entity, getCommonProps, getFieldOperator, getFieldProps, getHasAdvancedFilters, getOperatorValues, hasAdvancedFilters, language, t, theme, values]);
  const renderFields = useCallback(_ref6 => {
    let {
        fields
      } = _ref6,
      otherProps = _objectWithoutProperties(_ref6, _excluded);
    return (fields === null || fields === void 0 ? void 0 : fields.length) > 0 ? fields.map(field => renderField(_objectSpread(_objectSpread({}, otherProps), {}, {
      field
    }))) : null;
  }, [renderField]);
  const renderSubfilters = useCallback(_ref7 => {
    let {
        renderer,
        className,
        schema
      } = _ref7,
      otherProps = _objectWithoutProperties(_ref7, _excluded2);
    if (!(schema !== null && schema !== void 0 && schema.length) || !renderer) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: className
    }, renderer(_objectSpread(_objectSpread({}, otherProps), {}, {
      schema,
      useSections: false,
      renderingSubfilters: true
    })));
  }, []);
  const renderFilters = useCallback(_ref8 => {
    let {
        handleChangeOperator,
        handleOnBlur,
        handleOnChange,
        handleOnChangeGroup,
        handleOnDelete,
        innerValues
      } = _ref8,
      override = _objectWithoutProperties(_ref8, _excluded3);
    const finalSchema = override.schema || schema;
    if (!finalSchema.length) return;
    const finalUseSections = override.hasOwnProperty('useSections') ? override.useSections : useSections;
    const finalToggled = override.toggled || toggled;
    const isSubfilter = override.renderingSubfilters;
    const content = (finalSchema === null || finalSchema === void 0 ? void 0 : finalSchema.length) > 0 && finalSchema.map((filter, index) => {
      var _finalToggled$ID;
      let canBePinned = isPinnable;
      if (filter.hasOwnProperty('isPinnable')) canBePinned = filter.isPinnable;
      const ID = filter.crossId || filter.id;
      const headerPostComponent = canBePinned ? /*#__PURE__*/React.createElement(Tooltip, {
        content: t('label_filters_pin_desc'),
        placement: "top"
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
        className: classes.iconSection,
        name: "pin",
        onClick: onToggleQuickFilter(ID)
      }))) : undefined;
      const hasSubfilters = !!filter.subfilterSchema;
      let isOpen = false;
      if (finalToggled.hasOwnProperty(ID)) {
        const toggledFilter = finalToggled[ID];
        isOpen = typeof toggledFilter === 'boolean' ? toggledFilter : toggledFilter.open;
      }
      const renderProps = {
        getFieldProps,
        handleChangeOperator,
        handleOnBlur,
        handleOnChange,
        handleOnChangeGroup,
        innerValues
      };
      const activeFields = getActiveFields(filter);
      const sectionClasses = classNames(classes.filter, filter.className ? filter.className : `section-filter__field__${ID}`, {
        [classes.subFilterHeader]: isSubfilter
      });
      return /*#__PURE__*/React.createElement(Section, {
        key: index,
        className: sectionClasses,
        title: isSubfilter && activeFields > 0 ? `${filter.label} (${activeFields})` : filter.label,
        isOpen: isOpen,
        onChange: handleOnToggleFilter({
          filter: ID,
          hasSubfilters,
          parentFilter: override.parentFilter
        }),
        activeFields: !isSubfilter && activeFields,
        headerPostComponent: headerPostComponent,
        onRemove: filter.isRemovable ? handleOnDelete && handleOnDelete(filter) : undefined,
        classes: {
          header: classes.filterHeader,
          open: isOpen ? classes.filterHeaderOpen : null
        }
      }, renderFields(_objectSpread(_objectSpread({}, renderProps), {}, {
        crossEntity: filter.entity,
        fields: filter.fields,
        renderingSubfilters: override === null || override === void 0 ? void 0 : override.renderingSubfilters
      })), renderSubfilters(_objectSpread(_objectSpread({}, renderProps), {}, {
        className: classes.subFilters,
        parentFilter: ID,
        renderer: renderFilters,
        schema: filter.subfilterSchema,
        toggled: ((_finalToggled$ID = finalToggled[ID]) === null || _finalToggled$ID === void 0 ? void 0 : _finalToggled$ID.subfilters) || {},
        renderingSubfilters: true
      })));
    });
    return finalUseSections ? /*#__PURE__*/React.createElement(FiltersSection, {
      title: override.title || t('label_filters_section_filters'),
      filterNum: finalSchema === null || finalSchema === void 0 ? void 0 : finalSchema.length
    }, content) : /*#__PURE__*/React.createElement(Fragment, null, content);
  }, [schema, useSections, toggled, t, isPinnable, classes.iconSection, classes.filter, classes.filterHeader, classes.filterHeaderOpen, classes.subFilters, classes.subFilterHeader, getFieldProps, handleOnToggleFilter, getActiveFields, renderFields, renderSubfilters, onToggleQuickFilter]);
  const hasOperatorsApplied = useMemo(() => {
    if (!operators && !crossOperators) return false;
    const checkOperators = ops => {
      const keys = Object.keys(ops || {});
      const arr = ['empty', 'notempty'];
      if (keys.length) {
        for (let key of keys) {
          var _ops$key, _ops$key$value;
          if (arr.includes((_ops$key = ops[key]) === null || _ops$key === void 0 ? void 0 : (_ops$key$value = _ops$key.value) === null || _ops$key$value === void 0 ? void 0 : _ops$key$value.toLowerCase())) {
            return true;
          }
        }
      }
      return false;
    };

    // Check operators
    if (checkOperators(operators)) {
      return true;
    }

    // Check crossOperators
    for (let key in crossOperators) {
      if (checkOperators(crossOperators[key])) {
        return true;
      }
    }
    return false;
  }, [crossOperators, operators]);
  const hasFiltersApplied = useMemo(() => {
    if (!values && !hasOperatorsApplied || Object.keys(values).length === 0 && !hasOperatorsApplied) return false;
    return true;
  }, [hasOperatorsApplied, values]);
  return {
    getActiveFields,
    getFieldProps,
    handleOnToggleFilter,
    hasFiltersApplied,
    onToggleQuickFilter,
    renderFields,
    renderFilters,
    renderSubfilters
  };
};
export default useFilters;